import Loading from "components/Loading";
import PageContent from "components/PageContent";
import PageSecondaryNavigation, {
  PageNavLink,
} from "components/PageSecondaryNavigation";
import { useCurrentServer } from "components/WithCurrentOrganization";
import React, { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useRoutes } from "utils/routes";
import {
  GetQueryAdvisorDataQuery,
  useGetQueryAdvisorDataQuery,
} from "./gql/Query.generated";
import Grid, { MsCell, NumberCell } from "components/Grid";
import {
  formatNumber,
  formatPercent,
  formatTimestampShort,
} from "utils/format";
import Popover from "components/Popover";
import ImpactRenderer, { Impact } from "components/ImpactRenderer";
import Panel from "components/Panel";
import QueryAdvisorBlankSlate from "components/QueryAdvisorBlankSlate";
import Badge from "components/Badge";
import moment from "moment";
import classNames from "classnames";
import PanelTileGroup, { PanelTile } from "components/PanelTileGroup";
import Tip from "components/Tip";
import { getAnnotationTitle, insightTypeToAnnotationType } from "utils/explain";

export type QueryAdvisorTabType = "explain" | "workbooks";

const QueryAdvisor = ({ tab }: { tab: QueryAdvisorTabType }) => {
  const server = useCurrentServer();
  const serverId = server.humanId;
  const { databaseId } = useParams();
  const { databaseQueriesAdvisor, databaseQueriesAdvisorWorkbooks } =
    useRoutes();
  const now = useMemo(() => moment(), []);

  const { data, loading, error } = useGetQueryAdvisorDataQuery({
    variables: {
      serverId,
      databaseId,
      startTs: now.clone().subtract(7, "d").unix(),
      endTs: now.unix(),
    },
  });

  const featureNav = (
    <PageSecondaryNavigation>
      <PageNavLink
        to={databaseQueriesAdvisor(databaseId)}
      >{`Automated EXPLAIN (${data?.getIssues.length ?? "?"})`}</PageNavLink>
      <PageNavLink to={databaseQueriesAdvisorWorkbooks(databaseId)}>
        {`Workbooks with Insights (${data?.getWorkbooks.length ?? "?"})`}
      </PageNavLink>
    </PageSecondaryNavigation>
  );

  return (
    <PageContent
      title="Query Advisor"
      pageCategory="queries"
      pageName="advisor"
      featureNav={featureNav}
      pageTab={tab}
    >
      {loading || error ? (
        <Loading error={!!error} />
      ) : (
        <QueryAdvisorContent
          tab={tab}
          databaseId={databaseId}
          serverId={serverId}
          issues={data.getIssues}
          explainInfo={data.getQueryExplainStats}
          workbooks={data.getWorkbooks}
          canCreateWorkbooks={
            data.getDatabaseDetails.permittedToCreateWorkbooks
          }
        />
      )}
    </PageContent>
  );
};

const QueryAdvisorContent = ({
  tab,
  databaseId,
  serverId,
  issues,
  explainInfo,
  workbooks,
  canCreateWorkbooks,
}: {
  tab: QueryAdvisorTabType;
  databaseId: string;
  serverId: string;
  issues: GetQueryAdvisorDataQuery["getIssues"];
  explainInfo: GetQueryAdvisorDataQuery["getQueryExplainStats"];
  workbooks: GetQueryAdvisorDataQuery["getWorkbooks"];
  canCreateWorkbooks: boolean;
}) => {
  return (
    <>
      {tab === "explain" && (
        <>
          <KPITiles explainInfo={explainInfo} />
          {issues.length == 0 ? (
            <QueryAdvisorBlankSlate
              databaseId={databaseId}
              serverId={serverId}
            />
          ) : (
            <QueryAdvisorIssueList
              databaseId={databaseId}
              issues={issues}
              explainInfo={explainInfo}
            />
          )}
        </>
      )}
      {tab === "workbooks" && (
        <QueryAdvisorWorkbookList
          databaseId={databaseId}
          workbooks={workbooks}
          canCreateWorkbooks={canCreateWorkbooks}
        />
      )}
    </>
  );
};

const QueryAdvisorIssueList = ({
  databaseId,
  issues,
  explainInfo,
}: {
  databaseId: string;
  issues: GetQueryAdvisorDataQuery["getIssues"];
  explainInfo: GetQueryAdvisorDataQuery["getQueryExplainStats"];
}) => {
  const { databaseIssue } = useRoutes();
  const gridData = issues.map((issue) => {
    const queryId = issue.references.find(
      (ref) => ref.kind === "Query",
    ).referentId;
    const stats = explainInfo.stats.find((stat) => stat.id === queryId);
    const { details } = issue;
    return {
      id: issue.id,
      pctOfTotal: stats?.pctOfTotal ?? 0,
      normalizedQuery: issue.queryText,
      insights: details.insight_types,
      sampleCount: details.sample_count,
      sampleCountMore: details.more_samples,
      sampleMaxRuntime: details.max_runtime_ms,
      callsPerMinute: stats?.callsPerMinute ?? 0,
    };
  });
  return (
    <Panel title={`Queries with Insights (${issues.length})`}>
      <Grid
        striped
        data={gridData}
        defaultSortBy="pctOfTotal"
        columns={[
          {
            header: "Impact",
            field: "pctOfTotal",
            width: "81px",
            className: "flex justify-center",
            defaultSortOrder: "DESC",
            renderer: function Impact({ rowData: { id }, fieldData }) {
              return (
                <Link
                  to={databaseIssue(databaseId, id, "queries/advisor_insight")}
                >
                  <QueryAdvisorImpact pctOfTotal={fieldData} />
                </Link>
              );
            },
          },
          {
            header: "Query",
            field: "normalizedQuery",
            width: "3fr",
            style: "query",
            renderer: function IssueLinkCell({
              rowData: { id },
              fieldData: normalizedQuery,
            }) {
              return (
                <Link
                  to={databaseIssue(databaseId, id, "queries/advisor_insight")}
                >
                  {normalizedQuery}
                </Link>
              );
            },
          },
          {
            header: "Insights",
            field: "insights",
            width: "280px",
            nullValue: "n/a",
            defaultSortOrder: "DESC",
            renderer: function InsightsCell({ fieldData }) {
              return fieldData
                .map((t: string) =>
                  getAnnotationTitle(insightTypeToAnnotationType(t)),
                )
                .join(",");
            },
          },
          {
            header: "Samples",
            field: "sampleCount",
            width: "100px",
            nullValue: "n/a",
            defaultSortOrder: "DESC",
            style: "number",
            renderer: function Impact({
              rowData: { id, sampleCountMore },
              fieldData: sampleCount,
            }) {
              return (
                <Link
                  to={databaseIssue(databaseId, id, "queries/advisor_insight")}
                >
                  {sampleCountMore ? `${sampleCount}+` : sampleCount}
                </Link>
              );
            },
          },
          {
            header: "Max Runtime",
            field: "sampleMaxRuntime",
            width: "150px",
            nullValue: "n/a",
            defaultSortOrder: "DESC",
            style: "number",
            renderer: MsCell,
          },
          {
            header: "Calls / Min",
            field: "callsPerMinute",
            width: "150px",
            style: "number",
            defaultSortOrder: "DESC",
            renderer: NumberCell.precision(2),
          },
          {
            header: "% of all runtime",
            field: "pctOfTotal",
            width: "150px",
            style: "number",
            renderer: ({ fieldData }) => formatPercent(fieldData / 100),
            defaultSortOrder: "DESC",
          },
        ]}
      />
    </Panel>
  );
};

const QueryAdvisorWorkbookList = ({
  databaseId,
  workbooks,
  canCreateWorkbooks,
}: {
  databaseId: string;
  workbooks: GetQueryAdvisorDataQuery["getWorkbooks"];
  canCreateWorkbooks: boolean;
}) => {
  const { databaseWorkbookVariants, databaseWorkbooksNew } = useRoutes();
  return (
    <>
      <div className="flex justify-end mb-2">
        <Popover
          content={
            !canCreateWorkbooks && (
              <div>
                You are not allowed to create new workbooks: you do not have the{" "}
                <strong>Create Workbooks</strong> permission on this database.
              </div>
            )
          }
        >
          <Link
            to={databaseWorkbooksNew(databaseId)}
            className={classNames(
              "btn btn-success",
              !canCreateWorkbooks && "pointer-events-none opacity-[.65]",
            )}
          >
            New Workbook
          </Link>
        </Popover>
      </div>
      <Panel title={`Workbooks with Insights (${workbooks.length})`}>
        <Grid
          striped
          data={workbooks}
          defaultSortBy="lastActivityAt"
          columns={[
            {
              field: "name",
              header: "Workbook",
              width: "1fr",
              renderer: function NameCell({ rowData, fieldData }) {
                return (
                  <div className="flex flex-col">
                    <div className="flex">
                      <Link
                        to={databaseWorkbookVariants(databaseId, rowData.id)}
                      >
                        {fieldData}
                      </Link>
                      {rowData.draft && <Badge className="ml-2">Draft</Badge>}
                    </div>
                    {rowData.user?.fullname}
                  </div>
                );
              },
            },
            {
              header: "Insights",
              field: "insights",
              width: "230px",
              nullValue: "n/a",
              defaultSortOrder: "DESC",
              renderer: function InsightsCell({ fieldData }) {
                return fieldData
                  .map((t: string) =>
                    getAnnotationTitle(insightTypeToAnnotationType(t)),
                  )
                  .join(",");
              },
            },
            {
              header: "Variants",
              field: "variantsCount",
              width: "200px",
              style: "number",
            },
            {
              field: "lastActivityAt",
              header: "Last Activity",
              width: "300px",
              defaultSortOrder: "DESC",
              className: "text-right",
              headerClassName: "text-right",
              renderer: function LastActivityCell({ fieldData }) {
                return formatTimestampShort(moment.unix(fieldData));
              },
            },
          ]}
        />
      </Panel>
    </>
  );
};

const KPITiles = ({
  explainInfo,
}: {
  explainInfo: GetQueryAdvisorDataQuery["getQueryExplainStats"];
}) => {
  let totalPct = 0;
  const queryStats = explainInfo.stats;
  queryStats.forEach((val) => {
    if (val.explainExists) {
      totalPct += val.pctOfTotal;
    }
  });
  const queryCountWithExplain = queryStats.filter(
    (val) => val.explainExists,
  ).length;

  return (
    <PanelTileGroup className="mb-5">
      <PanelTile className="min-h-[140px]" title="Captured EXPLAIN Plans">
        <div className="mt-3 whitespace-nowrap">
          <span className="text-2xl mr-2">
            {formatNumber(explainInfo.totalExplainCount)}
          </span>
        </div>
        <div className="mt-2 whitespace-nowrap">in the last 7 days</div>
      </PanelTile>
      <PanelTile className="min-h-[140px]" title="Queries with EXPLAIN Plans">
        <div className="mt-3 whitespace-nowrap">
          <span className="text-2xl mr-2">
            {formatNumber(queryCountWithExplain)} /{" "}
            {formatNumber(queryStats.length)}
          </span>
        </div>
        <div className="mt-2 whitespace-nowrap">
          in the last 7 days{" "}
          <Tip content="The number of unique queries for which EXPLAIN Plans were recorded by Automated EXPLAIN." />
        </div>
      </PanelTile>
      <PanelTile
        className="min-h-[140px]"
        title="% of Query Runtime with EXPLAIN Plans"
      >
        <div className="mt-3 whitespace-nowrap">
          <span className="text-2xl mr-2">{formatPercent(totalPct / 100)}</span>
        </div>
        <div className="mt-2 whitespace-nowrap">
          in the last 7 days{" "}
          <Tip content="The sum of runtime percentage of queries for which EXPLAIN Plans are available." />
        </div>
      </PanelTile>
    </PanelTileGroup>
  );
};

export const QueryAdvisorImpact = ({
  pctOfTotal,
}: {
  pctOfTotal: GetQueryAdvisorDataQuery["getQueryExplainStats"]["stats"][number]["pctOfTotal"];
}) => {
  let impact: Impact = 1;
  let effect = "minimal";
  if (pctOfTotal > 1) {
    impact = 5;
    effect = "very large";
  } else if (pctOfTotal > 0.5) {
    impact = 4;
    effect = "large";
  } else if (pctOfTotal > 0.1) {
    impact = 3;
    effect = "medium";
  } else if (pctOfTotal > 0.01) {
    impact = 2;
    effect = "small";
  }
  return (
    <Popover
      content={
        <div>
          This query affects a {effect} part of the database's overall runtime:{" "}
          {formatPercent(pctOfTotal / 100)}
        </div>
      }
    >
      <ImpactRenderer impact={impact} />
    </Popover>
  );
};

export default QueryAdvisor;
